import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "../motion/Reveal";

export const NAV_LINKS = [
  { to: "/", label: "The Seven Laws", id: "laws" },
  { to: "/daily-life", label: "Daily Life", id: "daily-life" },
  { to: "/faq", label: "FAQ", id: "faq" },
  { to: "/study", label: "Study", id: "study" },
  { to: "/moshiach", label: "Moshiach", id: "moshiach" },
  { to: "/resources", label: "Resources", id: "resources" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
    <svg viewBox="0 0 40 24" className="h-5 w-8 text-terracotta" aria-hidden="true">
      {[6, 10, 14, 18].map((r, i) => (
        <path
          key={r}
          d={`M ${20 - r} 22 A ${r} ${r} 0 0 1 ${20 + r} 22`}
          stroke="currentColor"
          strokeWidth="1.4"
          fill="none"
          strokeOpacity={1 - i * 0.2}
        />
      ))}
    </svg>
    <span className="font-serif text-2xl font-medium tracking-tight text-ink">One Essence</span>
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b hairline bg-parchment/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Logo />
          <nav className="hidden items-center gap-7 md:flex lg:gap-9" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `relative text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-terracotta" : "text-ink-soft hover:text-ink"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-active"
                        className="absolute -bottom-1.5 left-0 h-px w-full bg-terracotta"
                        transition={{ duration: 0.5, ease: EASE }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <button
            type="button"
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="flex h-10 w-10 items-center justify-center rounded-full border hairline text-ink transition-colors hover:bg-parchment-deep md:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col justify-end bg-parchment px-6 pb-14 pt-28 md:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.1 + i * 0.07, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `flex items-center justify-between border-b hairline py-4 font-serif text-4xl ${
                        isActive ? "text-terracotta" : "text-ink"
                      }`
                    }
                  >
                    {l.label}
                    <ArrowUpRight size={22} className="text-ink-muted" />
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <p className="eyebrow mt-10">Genesis 9 · The Covenant of the Rainbow</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
